// ProviderScreen.js — Data table refactor

import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { API_IP } from "../env";
import ServicesOfProvidersFrom from "./Form/ServicesOfProvidersForm";
import NewProviderForm from "./Form/NewProviderForm";
import NewProviderRequestForm from "./Form/NewProviderRequestForm";
import ProfileChangeRequestsForm from "./Form/ProfileChangeRequestsForm";
import ProviderDetailForm from "./Form/ProviderDetailForm";
import AskingForm from "./Form/AskingForm";

import "./ProviderScreen.css";

const FILTERS = [
  { id: "all",        label: "All",        icon: "bi-grid" },
  { id: "verified",   label: "Verified",   icon: "bi-patch-check-fill" },
  { id: "unverified", label: "Pending",    icon: "bi-clock-history" },
];

function ProviderScreen({ globalSearch = "" }) {
  const [refreshProvider, setRefreshProvider] = useState(false);
  const [providerData, setProviderData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [showServicesForm, setShowServicesForm] = useState(false);
  const [showNewProviderForm, setShowNewProviderForm] = useState(false);
  const [showRequestsForm, setShowRequestsForm] = useState(false);
  const [showProfileChangeForm, setShowProfileChangeForm] = useState(false);
  const [showDetailForm, setShowDetailForm] = useState(false);
  const [showAskingForm, setShowAskingForm] = useState(false);

  const [selectedProviderId, setSelectedProviderId] = useState();
  const [selectedProviderName, setSelectedProviderName] = useState();
  const [newRequestCount, setRequestCount] = useState(0);
  const [profileChangeCount, setProfileChangeCount] = useState(0);
  const [filter, setFilter] = useState("all");

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    return await axios({ url, method, headers: { Authorization: `Bearer ${bearerToken}` }, data });
  }

  async function getAllProviders() {
    setIsLoading(true);
    try {
      const { data } = await axiosReqConfig(`${API_IP}/api/admin/providers`, "get", []);
      setProviderData(data);
    } catch (err) {
      console.error("Failed to fetch providers", err);
    }
    setIsLoading(false);
  }

  async function fetchRequestCount() {
    try {
      const { data } = await axiosReqConfig(`${API_IP}/api/admin/providers/requests`, "get", []);
      setRequestCount(data.length);
    } catch (err) { /* ignore */ }
  }

  async function fetchProfileChangeCount() {
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/profile-change-requests`,
        "get",
        []
      );
      setProfileChangeCount((data || []).length);
    } catch (err) { /* ignore */ }
  }

  useEffect(() => {
    getAllProviders();
    fetchRequestCount();
    fetchProfileChangeCount();
  }, [refreshProvider]);

  const counts = useMemo(() => ({
    all:        providerData.filter((i) => i.deactive === false).length,
    verified:   providerData.filter((i) => i.verification === true && i.deactive === false).length,
    unverified: providerData.filter((i) => i.verification === false && i.deactive === false).length,
  }), [providerData]);

  const filteredProviders = useMemo(() => {
    const q = (searchQuery || globalSearch || "").toLowerCase();
    return providerData.filter((p) => {
      if (p.deactive === true) return false;
      if (filter === "verified" && !(p.verification === true)) return false;
      if (filter === "unverified" && !(p.verification === false)) return false;
      if (q && !(p.provider_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [providerData, filter, searchQuery, globalSearch]);

  const openDetailModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowDetailForm(true);
  };
  const openServicesModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowServicesForm(true);
  };
  const openDeleteModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowAskingForm(true);
  };

  return (
    <>
      <div className="ps-screen">
        {/* ── Page Header ── */}
        <div className="page-header">
          <div>
            <h1 className="page-header__title">Providers</h1>
            <div className="page-header__subtitle">
              Manage and monitor every registered care provider
            </div>
          </div>
          <div className="page-header__right">
            <button className="ov-quick-btn" onClick={() => setShowRequestsForm(true)}>
              <i className="bi bi-bell" /> Requests
              {newRequestCount > 0 && (
                <span className="ov-quick-btn__badge" style={{ background: "var(--red)", color: "#fff" }}>
                  {newRequestCount}
                </span>
              )}
            </button>
            <button className="ov-quick-btn" onClick={() => setShowProfileChangeForm(true)}>
              <i className="bi bi-pencil-square" /> Profile Changes
              {profileChangeCount > 0 && (
                <span className="ov-quick-btn__badge" style={{ background: "var(--red)", color: "#fff" }}>
                  {profileChangeCount}
                </span>
              )}
            </button>
            <button className="ov-quick-btn ov-quick-btn--accent" onClick={() => setShowNewProviderForm(true)}>
              <i className="bi bi-plus-lg" /> New Provider
            </button>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Total in Network</div>
              <div className="stat-card__icon stat-card__icon--blue"><i className="bi bi-people-fill" /></div>
            </div>
            <div className="stat-card__value">{providerData.length}</div>
            <div className="stat-card__delta stat-card__delta--info">
              <i className="bi bi-graph-up" /> All providers including inactive
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Verified</div>
              <div className="stat-card__icon stat-card__icon--green"><i className="bi bi-patch-check-fill" /></div>
            </div>
            <div className="stat-card__value">{counts.verified}</div>
            <div className="stat-card__delta stat-card__delta--up">
              <i className="bi bi-check-circle" /> Active and approved
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Pending Verification</div>
              <div className="stat-card__icon stat-card__icon--amber"><i className="bi bi-clock-history" /></div>
            </div>
            <div className="stat-card__value">{counts.unverified}</div>
            <div className="stat-card__delta stat-card__delta--down">
              <i className="bi bi-exclamation-circle" /> Awaiting admin review
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">New Requests</div>
              <div className="stat-card__icon stat-card__icon--red"><i className="bi bi-bell-fill" /></div>
            </div>
            <div className="stat-card__value">{newRequestCount}</div>
            <div className="stat-card__delta stat-card__delta--down">
              <i className="bi bi-envelope-paper" /> Submissions to review
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="dt-toolbar">
          <div className="dt-toolbar__left">
            <div className="dt-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  className={`dt-filter-pill ${filter === f.id ? "dt-filter-pill--active" : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  <i className={`bi ${f.icon}`} />
                  {f.label}
                  <span className="dt-filter-pill__count">{counts[f.id] || 0}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="dt-toolbar__right">
            <div className="dt-search">
              <i className="bi bi-search dt-search__icon" />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Provider Table ── */}
        <div className="dt-shell">
          {isLoading ? (
            <div className="dt-loading">
              <div className="spinner-border" />
              <span>Loading providers…</span>
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="dt-empty">
              <div className="dt-empty__icon"><i className="bi bi-person-slash" /></div>
              <h4>No providers found</h4>
              <p>{searchQuery || globalSearch ? "Try a different search term." : "No providers match this filter."}</p>
            </div>
          ) : (
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Contact</th>
                  <th>Experience</th>
                  <th className="dt-th--right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProviders.map((p) => {
                  const initials = (p.provider_name || "?")
                    .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

                  return (
                    <tr key={p.provider_id}>
                      <td>
                        <div className="dt-user">
                          <div className="dt-avatar">
                            {p.img_url
                              ? <img src={p.img_url} alt={p.provider_name} />
                              : <span>{initials}</span>}
                          </div>
                          <div className="dt-cell-stack">
                            <div className="dt-name">{p.provider_name || "Unnamed Provider"}</div>
                            <div className="dt-sub">{p.title || "Care Provider"} · <span className="dt-mono">#{p.provider_id}</span></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {p.verification ? (
                          <span className="dt-pill dt-pill--success">Verified</span>
                        ) : (
                          <span className="dt-pill dt-pill--warn">Pending</span>
                        )}
                      </td>
                      <td>
                        <div className="dt-cell-stack">
                          <div className="dt-name" style={{ fontSize: "0.82rem" }}>
                            <i className="bi bi-envelope" style={{ color: "var(--text-3)", marginRight: "5px", fontSize: "0.76rem" }} />
                            {p.email || "—"}
                          </div>
                          <div className="dt-sub">
                            <i className="bi bi-telephone" style={{ marginRight: "5px", fontSize: "0.72rem" }} />
                            {p.phone_number || "No phone"}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: "var(--text-2)", fontSize: "0.83rem", fontWeight: 500 }}>
                          {p.experience || "—"}
                        </span>
                      </td>
                      <td className="dt-td--right">
                        <div className="ps-row-actions">
                          <button
                            className="dt-action-btn dt-action-btn--accent"
                            title="View Details"
                            onClick={() => openDetailModal(p)}
                          >
                            <i className="bi bi-person-lines-fill" />
                          </button>
                          <button
                            className="dt-action-btn dt-action-btn--violet"
                            title="Manage Services"
                            onClick={() => openServicesModal(p)}
                          >
                            <i className="bi bi-card-checklist" />
                          </button>
                          <button
                            className="dt-action-btn dt-action-btn--danger"
                            title="Delete Provider"
                            onClick={() => openDeleteModal(p)}
                          >
                            <i className="bi bi-trash3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!isLoading && filteredProviders.length > 0 && (
            <div className="dt-footer">
              <div>
                <span className="dt-pager-info">{filteredProviders.length}</span> shown
                <span style={{ margin: "0 10px", color: "var(--border-3)" }}>·</span>
                <span className="dt-pager-info">{providerData.length}</span> total in network
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-3)" }}>
                <i className="bi bi-info-circle" /> Click a row action to manage
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <AskingForm
        show={showAskingForm}
        onHide={() => setShowAskingForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
      <ServicesOfProvidersFrom
        show={showServicesForm}
        onHide={() => setShowServicesForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
      />
      <NewProviderForm
        show={showNewProviderForm}
        onHide={() => setShowNewProviderForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
      <NewProviderRequestForm
        show={showRequestsForm}
        onHide={() => setShowRequestsForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
      <ProfileChangeRequestsForm
        show={showProfileChangeForm}
        onHide={() => setShowProfileChangeForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
      <ProviderDetailForm
        show={showDetailForm}
        onHide={() => setShowDetailForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
    </>
  );
}

export default ProviderScreen;
